import axios, { InternalAxiosRequestConfig } from 'axios';
import { networkMonitor, SLOW_REQUEST_THRESHOLD_MS } from 'utils/networkMonitor';

// Extend Axios config to include custom properties
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _isLoginRequest?: boolean;
  __slowTimer?: ReturnType<typeof setTimeout>;
  __slowBannerShown?: boolean;
}

// Create an Axios instance with custom configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.forecastinn.com',
  // headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 seconds timeout
  withCredentials: false, // Change to true if you need to send cookies with requests
});

// Request interceptor for API calls
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set Content-Type only for non-GET requests and if not FormData
    if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
    }

    // Slow-request detection: show banner if still pending after threshold
    const cfg = config as CustomAxiosRequestConfig;
    cfg.__slowTimer = setTimeout(() => {
      cfg.__slowBannerShown = true;
      networkMonitor.incrementSlow();
    }, SLOW_REQUEST_THRESHOLD_MS);

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Helper: clear slow-request timer and decrement counter if banner was shown
const clearSlowTimer = (config: CustomAxiosRequestConfig | undefined) => {
  if (!config) return;
  if (config.__slowTimer) clearTimeout(config.__slowTimer);
  if (config.__slowBannerShown) {
    networkMonitor.decrementSlow();
    config.__slowBannerShown = false;
  }
};

// Response interceptor for API calls
api.interceptors.response.use(
  response => {
    clearSlowTimer(response.config as CustomAxiosRequestConfig);
    return response;
  },
  async error => {
    clearSlowTimer(error.config as CustomAxiosRequestConfig);
    const originalRequest = error.config as CustomAxiosRequestConfig;

    // Handle 401 Unauthorized errors (token expired)
    // Skip for login endpoint to avoid interfering with login error handling
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest._isLoginRequest &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      originalRequest._retry = true;

      // Clear auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('organization');
      localStorage.removeItem('organization_id');
      localStorage.removeItem('remember_me');
      localStorage.removeItem('remembered_user');

      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
export type { CustomAxiosRequestConfig };
